import dayjs from '@srm/ui/lib/dayjs';
import { diffCalendarDays, formatDayMonth } from './date';

/**
 * Форматирование дат/времени для ChatAI: «минимум необходимой информации» на виду,
 * подробная дата-время — в тултипе. Дату в ленте несёт разделитель дня
 * (см. {@link formatDaySeparator}), футер сообщения — только время.
 *
 * Все функции принимают ISO-строку / timestamp / Date и переданный `now` (для
 * детерминизма в тестах). Невалидный вход даёт пустую строку — вызывающий код
 * сам решает, рисовать ли элемент.
 */

type DateInput = string | number | Date;

const toDate = (input: DateInput): Date => dayjs(input).toDate();

const isValid = (d: Date): boolean => !Number.isNaN(d.getTime());

/**
 * Метка разделителя дня в ленте сообщений:
 * «Сегодня» / «Вчера» / «5 марта» / «5 марта 2025 г.» (год добавляется, если он
 * отличается от текущего). .locale('ru') явно — не зависим от глобальной локали.
 */
export function formatDaySeparator(input: DateInput, now: Date = new Date()): string {
    const date = toDate(input);
    if (!isValid(date)) return '';

    const diff = diffCalendarDays(date, now);
    if (diff === 0) return 'Сегодня';
    if (diff === 1) return 'Вчера';

    return formatDayMonth(date, now);
}

/** Короткое время сообщения в футере — ЧЧ:ММ (24ч, с ведущим нулём). */
export function formatMessageTime(input: DateInput): string {
    const date = toDate(input);
    if (!isValid(date)) return '';
    return dayjs(date).format('HH:mm');
}

/** Подробная дата-время для тултипов: «5 марта 2026 г., 14:30». */
export function formatDetailedDateTime(input: DateInput): string {
    const date = toDate(input);
    if (!isValid(date)) return '';
    return dayjs(date).locale('ru').format('D MMMM YYYY [г.], HH:mm');
}

/**
 * true, если `a` и `b` — разные календарные дни. Триггер для вставки разделителя
 * между соседними сообщениями (в т.ч. когда «оживает» старый тред: последнее
 * старое сообщение и новое сегодняшнее попадают в разные дни → разделитель).
 */
export function isDifferentCalendarDay(a: DateInput, b: DateInput): boolean {
    return !dayjs(a).isSame(dayjs(b), 'day');
}
